@use "sass:list";
@use "src/sedestral/sass/mixins/texts";

$reactions: (
  "happy": hop 1s,
  "love": pulse 1.4s,
  "laugh": laugh 1.2s,
  "wink": tilt 1.2s,
  "excited": double 1.4s,
  "proud": proud 1.6s,
  "curious": tilt 1.4s,
  "surprised": jump 1s,
  "confused": confused 1.6s,
  "suspicious": lean 1.6s,
  "angry": shake 1s,
  "sad": droop 1.8s,
  "scared": tremble 1.2s,
  "shy": shy 1.6s,
  "bored": sigh 2s,
  "sleepy": yawn 1.8s,
  "nod": nod 1.2s,
  "no": refuse 1.3s,
  "wave": greet 1.4s
);

.componentAgentAvatar {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  pointer-events: none;
  contain: layout style;
  container-type: inline-size;
  animation: avatarAppear .4s cubic-bezier(.3, .7, .3, 1) both;

  // retourné en miroir (posé à gauche, tourné vers le centre) : ses signes (ondes, regard, sonar) changent de côté, les
  // textes (pastille, réaction) restent lisibles
  &.mirrored {
    scale: -1 1;

    .glyph .symbol, .badgeDot {
      scale: -1 1;
    }
  }

  .shadow, .back, .body, .front {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  // l'ombre au sol faisait un trait gris sous chaque agent, et une tache sur le texte en grand format
  .shadow {
    display: none;
    inset: auto;
    left: 27%;
    width: 46%;
    top: 87%;
    height: 7%;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(0, 0, 0, .3), transparent);
  }

  .body {
    transform-origin: 50% 84%;
    animation: idleBody 3.9s ease-in-out var(--delay) infinite;
  }

  .reaction, .gaze, .scan, .lids {
    position: absolute;
    inset: 0;
  }

  .reaction {
    transform-origin: 50% 84%;
  }

  // les écrans habillent souvent « toute image » ou « tout svg » d'un bloc (cadre rond, bordure, marge, taille
  // d'icône, rotation), parfois en !important sous un sélecteur plus précis (marges des images d'article dans
  // l'éditeur) : posées dans une couche, ces protections du corps et des yeux l'emportent sur tout !important hors
  // couche, quel que soit son sélecteur ; l'opacité et le filtre restent à l'agent (hors ligne)
  @layer agentAvatar {
    .image {
      position: absolute !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      max-width: none !important;
      max-height: none !important;
      min-width: 0 !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      background: none !important;
      object-fit: contain !important;
      transform: none !important;
      translate: none !important;
      rotate: none !important;
      scale: none !important;
      animation: none !important;
      display: block !important;
      user-select: none;
      -webkit-user-drag: none;
    }

    .eyes {
      position: absolute !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      max-width: none !important;
      max-height: none !important;
      min-width: 0 !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      background: none !important;
      transform: none !important;
      translate: none !important;
      rotate: none !important;
      scale: none !important;
      animation: none !important;
      display: block !important;
      overflow: visible !important;
    }
  }

  .gaze {
    transition: transform .3s cubic-bezier(.3, .7, .3, 1);
  }

  .lids {
    transform-origin: 50% var(--eye-y);
    animation: blink var(--blink) linear var(--blink-delay) infinite;
  }

  .eyes {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .eye {
    fill: #17171d;
  }

  .glint {
    fill: #fff;
  }

  .orbit, .rings, .waves, .sonar, .think, .zzz, .write, .glyph, .particles {
    position: absolute;
    inset: 0;
    display: none;
  }

  // les signes d'un état (orbite, ondes, points, z…) apparaissent et s'effacent en fondu : un
  // changement d'état ne les fait plus surgir ni disparaître d'un coup
  .orbit, .rings, .waves, .sonar, .think, .zzz, .write, .particles {
    display: block;
    opacity: 0;
    visibility: hidden;
    transition: opacity .45s ease, visibility 0s linear .45s;
  }

  // les signes d'un état caché ne tournent pas pour rien (une trentaine d'agents à l'écran en faisait
  // tourner des centaines) : ils s'arrêtent une fois effacés, et reprennent où ils étaient en revenant
  &:not(.settling) {
    &:not([data-state="working"]) .orbit .dot,
    &:not([data-state="waiting"]):not([data-state="blocked"]) .rings .ring,
    &:not([data-state="listening"]) .waves .arc,
    &:not([data-state="searching"]) .sonar .arc,
    &:not([data-state="thinking"]) .think .dot,
    &:not([data-state="sleeping"]) .zzz .letter,
    &:not([data-state="writing"]) .write .line,
    &:not([data-state="writing"]) .write .pen {
      animation-play-state: paused;
    }
  }

  .orbit .dot {
    position: absolute;
    left: 47.8%;
    top: 50.8%;
    width: 4.4%;
    height: 4.4%;
    border-radius: 50%;
    background: var(--c);
    animation: orbitPath 1.9s linear infinite, orbitFront 1.9s linear infinite;

    &:nth-child(2) {
      animation-delay: -.633s;
    }

    &:nth-child(3) {
      animation-delay: -1.267s;
    }
  }

  .back .orbit .dot {
    animation-name: orbitPath, orbitBack;
  }

  .rings .ring {
    position: absolute;
    left: 18%;
    top: 22%;
    width: 64%;
    height: 64%;
    border: 2px solid var(--c);
    border-radius: 50%;
    opacity: 0;
    animation: ringWait 2.4s ease-out infinite;

    &:nth-child(2) {
      animation-delay: -1.2s;
    }
  }

  .waves .arc, .sonar .arc {
    position: absolute;
    top: 22%;
    width: 58%;
    height: 58%;
    border-radius: 50%;
    border: 2.5px solid transparent;
    opacity: 0;
  }

  .waves .arc {
    left: -8%;
    border-left-color: var(--c);
    animation: wave 1.4s ease-out infinite;

    &:nth-child(2) {
      animation-delay: -.7s;
    }
  }

  .sonar .arc {
    left: 50%;
    border-right-color: var(--c);
    animation: sonar 1.1s ease-out infinite;

    &:nth-child(2) {
      animation-delay: -.37s;
    }

    &:nth-child(3) {
      animation-delay: -.74s;
    }
  }

  .think .dot {
    position: absolute;
    top: 2%;
    width: 7%;
    height: 7%;
    border-radius: 50%;
    background: var(--c);
    animation: thinkDot 1.2s ease-in-out infinite;

    &:nth-child(1) {
      left: 62%;
    }

    &:nth-child(2) {
      left: 73%;
      animation-delay: .15s;
    }

    &:nth-child(3) {
      left: 84%;
      animation-delay: .3s;
    }
  }

  .zzz .letter {
    position: absolute;
    left: 64%;
    top: 20%;
    font-size: 14cqw;
    line-height: 1;
    color: var(--c);
    opacity: 0;
    animation: zzz 3s ease-out infinite;

    @include texts.font("bold");

    &:nth-child(2) {
      animation-delay: -1s;
      font-size: 11cqw;
    }

    &:nth-child(3) {
      animation-delay: -2s;
      font-size: 9cqw;
    }
  }

  .write {
    .line {
      position: absolute;
      left: 24%;
      top: 94%;
      width: 52%;
      height: 2.6%;
      border-radius: 2px;
      background: var(--c);
      transform-origin: left center;
      animation: writeLine 1.8s ease-in-out infinite;
    }

    .pen {
      position: absolute;
      left: 21%;
      top: 91.6%;
      width: 6%;
      height: 6%;
      border-radius: 50%;
      background: var(--c);
      animation: writePen 1.8s ease-in-out infinite;
    }
  }

  .glyph .symbol {
    position: absolute;
    left: 66%;
    top: 4%;
    font-size: 17cqw;
    line-height: 1;
    color: var(--glyph, var(--c));
    opacity: 0;

    @include texts.font("bold");
  }

  .badge {
    position: absolute;
    left: 72%;
    top: 15%;
    width: 14%;
    height: 14%;
    display: block;
    opacity: 0;
    visibility: hidden;
    transform: scale(.4);
    transition: opacity .3s ease, transform .3s ease, visibility 0s linear .3s;

    .badgeDot {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--badge);
      transition: background-color .3s ease;
      color: #fff;
      font-size: 9cqw;
      line-height: 1;

      @include texts.font("bold");
    }
  }

  .particles .particle {
    position: absolute;
    left: 48%;
    top: 48%;
    width: 3.6%;
    height: 3.6%;
    border-radius: 50%;
    opacity: 0;
  }

  &.burst .particles {
    opacity: 1;
    visibility: visible;
    transition: none;

    .particle {
      animation: burst .9s cubic-bezier(.2, .7, .3, 1) forwards;
    }
  }

  &[data-state="idle"]:not(.follow) .scan {
    animation: lookAround var(--look) ease-in-out var(--delay) infinite;
  }

  &[data-state="listening"] {
    .body {
      animation: listenBody 2s ease-in-out var(--delay) infinite;
    }

    .waves {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }
  }

  &[data-state="thinking"] {
    .body {
      animation: thinkBody 3s ease-in-out var(--delay) infinite;
    }

    .think {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }
  }

  &[data-state="searching"] {
    .body {
      animation: searchBody 1.6s ease-in-out var(--delay) infinite alternate;
    }

    .scan {
      animation: scanWide .8s ease-in-out infinite alternate;
    }

    .sonar {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }
  }

  &[data-state="working"] {
    .body {
      animation: workBody .9s ease-in-out var(--delay) infinite;
    }

    .shadow {
      animation: workShadow .9s ease-in-out var(--delay) infinite;
    }

    .scan {
      animation: scan 1.9s ease-in-out infinite alternate;
    }

    .orbit {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }
  }

  &[data-state="writing"] {
    .body {
      animation: writeBody .7s ease-in-out var(--delay) infinite;
    }

    .write {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }
  }

  &[data-state="waiting"] {
    .body {
      animation: waitBody 5.2s ease-in-out var(--delay) infinite;
    }

    .lids {
      animation-duration: 2.2s;
    }

    .rings {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }
  }

  &[data-state="notifying"] {
    .body {
      animation: notifyBody 2.2s ease-out var(--delay) infinite;
    }

    .badge {
      opacity: 1;
      visibility: visible;
      transform: none;
      transition: opacity .25s ease, transform .4s cubic-bezier(.3, 1.6, .5, 1);
    }

    .badgeDot {
      animation: ping 1.2s ease-in-out .45s infinite;
    }
  }

  &[data-state="blocked"] {
    .body {
      animation: blockBody 2.2s linear infinite;
    }

    .rings {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }

    .ring {
      border-color: #ffb020;
      animation: ringBlock 1.2s ease-out infinite;

      &:nth-child(2) {
        animation-delay: -.6s;
      }
    }

    .badge {
      opacity: 1;
      visibility: visible;
      transform: none;
      transition: opacity .25s ease, transform .4s cubic-bezier(.3, 1.6, .5, 1);
    }
  }

  &[data-state="error"] {
    .body {
      animation: errorBody 2.6s ease-in-out var(--delay) infinite;
    }

    .lids {
      animation: none;
    }

    .badge {
      opacity: 1;
      visibility: visible;
      transform: none;
      transition: opacity .25s ease, transform .4s cubic-bezier(.3, 1.6, .5, 1);
    }
  }

  &[data-state="done"] {
    .body {
      animation: doneBody 1.6s ease-out var(--delay) infinite;
    }

    .shadow {
      animation: doneShadow 1.6s ease-out var(--delay) infinite;
    }

    .lids {
      animation: none;
    }

    .badge {
      opacity: 1;
      visibility: visible;
      transform: none;
      transition: opacity .25s ease, transform .4s cubic-bezier(.3, 1.6, .5, 1);
    }
  }

  &[data-state="celebrate"] {
    .body {
      animation: celebrateBody 2.4s ease-in-out var(--delay) infinite;
    }

    .shadow {
      animation: celebrateShadow 2.4s ease-in-out var(--delay) infinite;
    }

    .lids {
      animation: none;
    }

    .particles {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }

    .badge {
      opacity: 1;
      visibility: visible;
      transform: none;
      transition: opacity .25s ease, transform .4s cubic-bezier(.3, 1.6, .5, 1);
    }

    .particle {
      animation: burstSoft 2.4s ease-out var(--delay) infinite;
    }
  }

  &[data-state="sleeping"] {
    .body {
      animation: sleepBody 4.2s ease-in-out var(--delay) infinite;
    }

    .lids {
      animation: none;
    }

    .zzz {
      opacity: 1;
      visibility: visible;
      transition: opacity .45s ease;
    }
  }

  &[data-state="offline"] {
    .body, .lids {
      animation: none;
    }

    .image {
      filter: grayscale(1);
      opacity: .5;
    }

    .eyes {
      opacity: .55;
    }
  }

  @each $name, $motion in $reactions {
    &[data-reaction="#{$name}"] .reaction {
      animation: list.nth($motion, 1) list.nth($motion, 2) ease-in-out;
    }
  }

  &[data-reaction] .glyph {
    display: block;

    .symbol {
      animation: glyphFloat 1.4s ease-out forwards;
    }
  }

  &.paused, &.paused * {
    animation-play-state: paused !important;
  }

  // un visage figé (historique, liste) se pose tel quel : son apparition gelée le laisserait invisible
  &.paused, &.appeared {
    animation: none;
  }

  @container (max-width: 56px) {
    .orbit .dot {
      left: 44%;
      top: 46%;
      width: 12%;
      height: 12%;
      animation-name: orbitPathSmall, orbitFront;
    }

    .back .orbit .dot {
      animation-name: orbitPathSmall, orbitBack;
    }

    .rings .ring {
      left: 2%;
      top: 6%;
      width: 96%;
      height: 96%;
      border-width: 1.5px;
    }

    .waves .arc, .sonar .arc {
      border-width: 1.5px;
    }

    .badge {
      left: 56%;
      top: -10%;
      width: 50%;
      height: 50%;
    }

    .badgeDot {
      font-size: 32cqw;
    }

    .think .dot {
      width: 13%;
      height: 13%;
      top: -10%;

      &:nth-child(1) {
        left: 50%;
      }

      &:nth-child(2) {
        left: 68%;
      }

      &:nth-child(3) {
        left: 86%;
      }
    }

    .zzz .letter {
      font-size: 30cqw;
      top: 0;
    }

    .write {
      .line {
        height: 7%;
        top: 96%;
      }

      .pen {
        display: none;
      }
    }

    .glyph .symbol {
      font-size: 36cqw;
      left: 60%;
      top: -18%;
    }

    .particles .particle {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
      transition: none !important;
    }
  }

  @keyframes avatarAppear {
    from {
      opacity: 0;
      transform: scale(.85);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @keyframes idleBody {
    0%, 100% {
      transform: translateY(0) rotate(-1.2deg) scale(1, 1);
    }
    50% {
      transform: translateY(-1.1%) rotate(1.2deg) scale(.988, 1.014);
    }
  }

  @keyframes listenBody {
    0%, 100% {
      transform: translateX(-1%) rotate(-5deg);
    }
    50% {
      transform: translateX(-1.6%) rotate(-3.5deg) scale(1.012, .99);
    }
  }

  @keyframes thinkBody {
    0%, 100% {
      transform: rotate(3deg);
    }
    50% {
      transform: translateY(-1%) rotate(6deg);
    }
  }

  @keyframes searchBody {
    from {
      transform: rotate(-4deg);
    }
    to {
      transform: rotate(4deg);
    }
  }

  @keyframes workBody {
    0%, 100% {
      transform: translateY(0) rotate(-1.4deg) scale(1.03, .97);
    }
    35% {
      transform: translateY(-2.2%) rotate(0deg) scale(.985, 1.02);
    }
    65% {
      transform: translateY(-.6%) rotate(1.4deg) scale(1, 1);
    }
  }

  @keyframes workShadow {
    0%, 100% {
      transform: scale(1);
    }
    35% {
      transform: scale(.9);
    }
  }

  @keyframes writeBody {
    0%, 100% {
      transform: rotate(2deg) translateY(0);
    }
    50% {
      transform: rotate(3.5deg) translateY(.9%);
    }
  }

  @keyframes waitBody {
    0%, 100% {
      transform: rotate(-4deg);
    }
    50% {
      transform: rotate(4deg);
    }
  }

  @keyframes notifyBody {
    0%, 40%, 100% {
      transform: none;
    }
    8% {
      transform: translateY(-3.5%) scale(.98, 1.02);
    }
    16% {
      transform: translateY(0) scale(1.03, .97);
    }
    22% {
      transform: rotate(-4deg);
    }
    28% {
      transform: rotate(4deg);
    }
    34% {
      transform: rotate(-2deg);
    }
  }

  @keyframes blockBody {
    0% {
      transform: translateX(0);
    }
    3% {
      transform: translateX(-2%);
    }
    6% {
      transform: translateX(2%);
    }
    9% {
      transform: translateX(-1.6%);
    }
    12% {
      transform: translateX(1.2%);
    }
    15% {
      transform: translateX(-.6%);
    }
    18%, 100% {
      transform: translateX(0);
    }
  }

  @keyframes errorBody {
    0%, 100% {
      transform: translateY(1.5%) rotate(5deg);
    }
    50% {
      transform: translateY(1.8%) rotate(7deg) scale(1.015, .98);
    }
  }

  @keyframes doneBody {
    0% {
      transform: translateY(0) scale(1, 1);
    }
    12% {
      transform: translateY(0) scale(1.06, .94);
    }
    38% {
      transform: translateY(-4.5%) scale(.97, 1.03);
    }
    60% {
      transform: translateY(0) scale(1.07, .93);
    }
    74% {
      transform: translateY(0) scale(.98, 1.02);
    }
    86%, 100% {
      transform: translateY(0) scale(1, 1);
    }
  }

  @keyframes doneShadow {
    0%, 12%, 60%, 100% {
      transform: scale(1);
    }
    38% {
      transform: scale(.72);
      opacity: .6;
    }
  }

  @keyframes celebrateBody {
    0%, 100% {
      transform: none;
    }
    14% {
      transform: scale(1.04, .96);
    }
    34% {
      transform: translateY(-4%) rotate(-3deg) scale(.98, 1.02);
    }
    52% {
      transform: translateY(0) scale(1.04, .96);
    }
    68% {
      transform: translateY(-2%) rotate(3deg);
    }
    84% {
      transform: none;
    }
  }

  @keyframes celebrateShadow {
    0%, 14%, 52%, 84%, 100% {
      transform: scale(1);
    }
    34% {
      transform: scale(.85);
    }
    68% {
      transform: scale(.92);
    }
  }

  @keyframes sleepBody {
    0%, 100% {
      transform: translateY(1.5%) rotate(5deg) scale(1.025, .975);
    }
    50% {
      transform: translateY(1.5%) rotate(5deg) scale(.99, 1.015);
    }
  }

  @keyframes blink {
    0%, 94%, 100% {
      transform: scaleY(1);
    }
    96.4% {
      transform: scaleY(.04);
    }
  }

  @keyframes lookAround {
    0%, 16% {
      transform: translate(0, 0);
    }
    22%, 38% {
      transform: translate(3.4%, 1.2%);
    }
    44%, 58% {
      transform: translate(-2.8%, -1%);
    }
    64%, 80% {
      transform: translate(1.2%, -1.8%);
    }
    86%, 100% {
      transform: translate(0, 0);
    }
  }

  @keyframes scan {
    from {
      transform: translate(-3.4%, 2.4%);
    }
    to {
      transform: translate(3.4%, 2.4%);
    }
  }

  @keyframes scanWide {
    from {
      transform: translate(-5.5%, 0);
    }
    to {
      transform: translate(5.5%, 0);
    }
  }

  @keyframes orbitPath {
    0% {
      transform: translate(886%, 0%) scale(1);
    }
    6.25% {
      transform: translate(819%, 78%) scale(1.11);
    }
    12.5% {
      transform: translate(626%, 145%) scale(1.21);
    }
    18.75% {
      transform: translate(339%, 189%) scale(1.28);
    }
    25% {
      transform: translate(0%, 205%) scale(1.3);
    }
    31.25% {
      transform: translate(-339%, 189%) scale(1.28);
    }
    37.5% {
      transform: translate(-626%, 145%) scale(1.21);
    }
    43.75% {
      transform: translate(-819%, 78%) scale(1.11);
    }
    50% {
      transform: translate(-886%, 0%) scale(1);
    }
    56.25% {
      transform: translate(-819%, -78%) scale(.89);
    }
    62.5% {
      transform: translate(-626%, -145%) scale(.79);
    }
    68.75% {
      transform: translate(-339%, -189%) scale(.72);
    }
    75% {
      transform: translate(0%, -205%) scale(.7);
    }
    81.25% {
      transform: translate(339%, -189%) scale(.72);
    }
    87.5% {
      transform: translate(626%, -145%) scale(.79);
    }
    93.75% {
      transform: translate(819%, -78%) scale(.89);
    }
    100% {
      transform: translate(886%, 0%) scale(1);
    }
  }

  @keyframes orbitPathSmall {
    0% {
      transform: translate(400%, 0%) scale(1);
    }
    6.25% {
      transform: translate(370%, 38%) scale(1.1);
    }
    12.5% {
      transform: translate(283%, 71%) scale(1.18);
    }
    18.75% {
      transform: translate(153%, 92%) scale(1.23);
    }
    25% {
      transform: translate(0%, 100%) scale(1.25);
    }
    31.25% {
      transform: translate(-153%, 92%) scale(1.23);
    }
    37.5% {
      transform: translate(-283%, 71%) scale(1.18);
    }
    43.75% {
      transform: translate(-370%, 38%) scale(1.1);
    }
    50% {
      transform: translate(-400%, 0%) scale(1);
    }
    56.25% {
      transform: translate(-370%, -38%) scale(.9);
    }
    62.5% {
      transform: translate(-283%, -71%) scale(.82);
    }
    68.75% {
      transform: translate(-153%, -92%) scale(.77);
    }
    75% {
      transform: translate(0%, -100%) scale(.75);
    }
    81.25% {
      transform: translate(153%, -92%) scale(.77);
    }
    87.5% {
      transform: translate(283%, -71%) scale(.82);
    }
    93.75% {
      transform: translate(370%, -38%) scale(.9);
    }
    100% {
      transform: translate(400%, 0%) scale(1);
    }
  }

  @keyframes orbitFront {
    0%, 49.9% {
      opacity: 1;
    }
    50%, 100% {
      opacity: 0;
    }
  }

  @keyframes orbitBack {
    0%, 49.9% {
      opacity: 0;
    }
    50%, 100% {
      opacity: 1;
    }
  }

  @keyframes ringWait {
    0% {
      transform: scale(1);
      opacity: .55;
    }
    100% {
      transform: scale(1.55);
      opacity: 0;
    }
  }

  @keyframes ringBlock {
    0% {
      transform: scale(1);
      opacity: .8;
    }
    100% {
      transform: scale(1.22);
      opacity: 0;
    }
  }

  @keyframes pop {
    0% {
      transform: scale(.2);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes ping {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.18);
    }
  }

  @keyframes wave {
    0% {
      transform: scale(.8);
      opacity: .9;
    }
    100% {
      transform: scale(1.3) translateX(-10%);
      opacity: 0;
    }
  }

  @keyframes sonar {
    0% {
      transform: scale(.7);
      opacity: .95;
    }
    100% {
      transform: scale(1.35) translateX(12%);
      opacity: 0;
    }
  }

  @keyframes thinkDot {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: .45;
    }
    30% {
      transform: translateY(-60%);
      opacity: 1;
    }
  }

  @keyframes zzz {
    0% {
      transform: translate(0, 0) scale(.6);
      opacity: 0;
    }
    25% {
      opacity: 1;
    }
    100% {
      transform: translate(70%, -160%) scale(1.15);
      opacity: 0;
    }
  }

  @keyframes writeLine {
    0% {
      transform: scaleX(0);
      opacity: 1;
    }
    75% {
      transform: scaleX(1);
      opacity: 1;
    }
    100% {
      transform: scaleX(1);
      opacity: 0;
    }
  }

  @keyframes writePen {
    0% {
      transform: translateX(0);
      opacity: 1;
    }
    75% {
      transform: translateX(866%);
      opacity: 1;
    }
    100% {
      transform: translateX(866%);
      opacity: 0;
    }
  }

  @keyframes glyphFloat {
    0% {
      transform: translate(0, 20%) scale(.5);
      opacity: 0;
    }
    20% {
      transform: translate(0, 0) scale(1.1);
      opacity: 1;
    }
    100% {
      transform: translate(25%, -90%) scale(1);
      opacity: 0;
    }
  }

  @keyframes burst {
    0% {
      transform: translate(0, 0) scale(1);
      opacity: 1;
    }
    100% {
      transform: translate(var(--dx), var(--dy)) scale(.4);
      opacity: 0;
    }
  }

  @keyframes burstSoft {
    0% {
      transform: translate(0, 0) scale(.8);
      opacity: 0;
    }
    15% {
      opacity: .9;
    }
    100% {
      transform: translate(calc(var(--dx) * .55), calc(var(--dy) * .55)) scale(.4);
      opacity: 0;
    }
  }

  @keyframes hop {
    0%, 100% {
      transform: none;
    }
    30% {
      transform: translateY(-3.5%) scale(.98, 1.02);
    }
    55% {
      transform: translateY(0) scale(1.04, .96);
    }
    75% {
      transform: scale(.99, 1.01);
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    20%, 60% {
      transform: scale(1.1);
    }
    40%, 80% {
      transform: scale(.98);
    }
  }

  @keyframes laugh {
    0%, 100% {
      transform: none;
    }
    10%, 30%, 50%, 70% {
      transform: translateY(-2.5%) rotate(-2deg);
    }
    20%, 40%, 60%, 80% {
      transform: translateY(0) rotate(2deg);
    }
  }

  @keyframes tilt {
    0%, 100% {
      transform: none;
    }
    25%, 75% {
      transform: rotate(9deg);
    }
  }

  @keyframes double {
    0%, 100% {
      transform: none;
    }
    15%, 45% {
      transform: translateY(-4%);
    }
    30%, 60% {
      transform: translateY(0) scale(1.03, .97);
    }
    75% {
      transform: none;
    }
  }

  @keyframes proud {
    0%, 100% {
      transform: none;
    }
    25%, 75% {
      transform: translateY(-2%) scale(1.04, 1.06);
    }
  }

  @keyframes jump {
    0%, 100% {
      transform: none;
    }
    15% {
      transform: translateY(-5%) scale(.95, 1.06);
    }
    35% {
      transform: translateY(0) scale(1.03, .97);
    }
    50% {
      transform: none;
    }
  }

  @keyframes confused {
    0%, 100% {
      transform: none;
    }
    20% {
      transform: rotate(-8deg);
    }
    45% {
      transform: rotate(8deg);
    }
    70% {
      transform: rotate(-4deg);
    }
  }

  @keyframes lean {
    0%, 100% {
      transform: none;
    }
    25%, 75% {
      transform: translateX(3%) rotate(5deg);
    }
  }

  @keyframes shake {
    0%, 100% {
      transform: none;
    }
    10%, 30%, 50%, 70% {
      transform: translateX(-1.6%) scale(1.03, .97);
    }
    20%, 40%, 60%, 80% {
      transform: translateX(1.6%) scale(1.03, .97);
    }
  }

  @keyframes droop {
    0%, 100% {
      transform: none;
    }
    25%, 80% {
      transform: translateY(3%) scale(1.03, .94) rotate(-3deg);
    }
  }

  @keyframes tremble {
    0%, 100% {
      transform: none;
    }
    5%, 15%, 25%, 35%, 45%, 55%, 65%, 75% {
      transform: translateX(-1.4%) scale(.94);
    }
    10%, 20%, 30%, 40%, 50%, 60%, 70%, 80% {
      transform: translateX(1.4%) scale(.94);
    }
  }

  @keyframes shy {
    0%, 100% {
      transform: none;
    }
    25%, 75% {
      transform: translateX(-3%) rotate(-7deg) scale(.93);
    }
  }

  @keyframes sigh {
    0%, 100% {
      transform: none;
    }
    30% {
      transform: scale(1.02, 1.04);
    }
    60%, 80% {
      transform: translateY(2%) scale(1.03, .95);
    }
  }

  @keyframes yawn {
    0%, 100% {
      transform: none;
    }
    35%, 60% {
      transform: scale(.97, 1.08) rotate(-3deg);
    }
  }

  @keyframes nod {
    0%, 40%, 80%, 100% {
      transform: none;
    }
    20%, 60% {
      transform: translateY(2.5%) rotate(4deg);
    }
  }

  @keyframes refuse {
    0%, 100% {
      transform: none;
    }
    15% {
      transform: rotate(-8deg);
    }
    35% {
      transform: rotate(8deg);
    }
    55% {
      transform: rotate(-6deg);
    }
    75% {
      transform: rotate(4deg);
    }
  }

  @keyframes greet {
    0%, 100% {
      transform: none;
    }
    15%, 45% {
      transform: rotate(10deg);
    }
    30%, 60% {
      transform: rotate(-8deg);
    }
    75% {
      transform: rotate(4deg);
    }
  }
}
